import { useState } from 'react';
import { BRIEF_QUESTIONS, type Brief } from '@studio/shared';
import type { TemplateSummary } from '../api.js';

const EMPTY: Brief = { world: '', character: '', action: '', winCondition: '', loseCondition: '' };

/**
 * Prompts under each question.
 *
 * These nudge toward being SPECIFIC without supplying any content — the whole
 * lesson is that specifying is the child's job. "A jungle at night, or a kitchen
 * cupboard?" teaches the shape of a good answer; offering a list to pick from
 * would do the thinking for them.
 */
const HINTS: Record<keyof Brief, string> = {
  world: 'A jungle at night? Inside a kitchen cupboard? Be exact.',
  character: 'What are you? What can you do that nothing else can?',
  action: 'The most important one. Do you jump? Fly? Build? Collect?',
  winCondition: 'What has to happen for you to win?',
  loseCondition: 'What goes wrong? What has to be avoided?',
};

const ORDER: (keyof Brief)[] = ['world', 'character', 'action', 'winCondition', 'loseCondition'];

type Props = {
  templates: TemplateSummary[];
  busy: boolean;
  onSubmit: (brief: Brief, templateId: string) => void;
};

export function BriefForm({ templates, busy, onSubmit }: Props) {
  const [brief, setBrief] = useState<Brief>(EMPTY);
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');

  const required: (keyof Brief)[] = ['world', 'character', 'action'];
  const ready = required.every((k) => brief[k].trim().length > 0) && templateId !== '';

  return (
    <form
      className="brief"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !busy) onSubmit(brief, templateId);
      }}
    >
      <h2>What are you making?</h2>

      <div className="brief__templates">
        {templates.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`template ${templateId === t.id ? 'template--on' : ''}`}
            onClick={() => setTemplateId(t.id)}
          >
            <strong>{t.name}</strong>
            <span>{t.blurb}</span>
          </button>
        ))}
      </div>

      {ORDER.map((key) => (
        <label key={key} className="field">
          <span className="field__q">
            {BRIEF_QUESTIONS[key]}
            {!required.includes(key) && <em> (optional)</em>}
          </span>
          <span className="field__hint">{HINTS[key]}</span>
          <textarea
            value={brief[key]}
            maxLength={300}
            rows={2}
            onChange={(e) => setBrief({ ...brief, [key]: e.target.value })}
            placeholder="…"
          />
        </label>
      ))}

      <button className="go" type="submit" disabled={!ready || busy}>
        {busy ? 'Building…' : 'Build it'}
      </button>
      {!ready && !busy && (
        <p className="brief__nudge">
          Fill in the world, who you are, and what you do — those three are how it knows what to make.
        </p>
      )}
    </form>
  );
}
